import cx from 'clsx';
import React from 'react';
import { Carousel, CarouselItem } from '@repo/shared-kit/components';
import {
  CustomPricePlan,
  type CustomPricePlanProps,
} from '../custom-price-plan/custom-price-plan.tsx';
import { ComparePlansLink } from './compare-plans-link.tsx';
import { IncludedInAllPlans } from './included-in-all-plans.tsx';
import type { Props as PricePlanProps } from './price-plan.tsx';
import { PricePlan } from './price-plan.tsx';
import styles from './price-plans-container.module.css';
import { SharedAddonsSection } from './shared-addons-section.tsx';

export interface PricePlansContainerProps {
  pricePlans: PricePlanProps[];
  customPricePlan?: CustomPricePlanProps;
  hasPromoInAnyProducts?: boolean;
  hidePromoHighlights?: boolean;
  hidePromoInAllProducts?: boolean;
  layout?: 'grid' | 'smallGrid';
  isOnboardingVariant?: boolean;
  isCompactLayout?: boolean;
  includedInAllPlans?: {
    title: string;
    benefits: string[];
  };
  sharedAddons?: {
    title: string;
    addons: {
      description: string;
      iconLightPath: string;
      iconDarkPath: string;
    }[];
  };
  comparePlansLink?: {
    text: string;
    href: string;
  };
}

export function PricePlansContainer({
  pricePlans,
  customPricePlan,
  hasPromoInAnyProducts,
  hidePromoHighlights,
  hidePromoInAllProducts,
  layout = 'grid',
  isOnboardingVariant,
  isCompactLayout,
  includedInAllPlans,
  sharedAddons,
  comparePlansLink,
  ...props
}: PricePlansContainerProps): React.JSX.Element {
  const gridClassName = layout === 'smallGrid' ? styles.containerSmall : styles.container;
  const containerClassName = cx(gridClassName, isCompactLayout && styles.compactContainer);

  const compactSections = isCompactLayout ? (
    <div className={styles.compactSections}>
      {includedInAllPlans || sharedAddons ? (
        <div className={styles.compactCard}>
          {includedInAllPlans ? (
            <IncludedInAllPlans
              benefits={includedInAllPlans.benefits}
              title={includedInAllPlans.title}
            />
          ) : null}
          {sharedAddons ? (
            <SharedAddonsSection addons={sharedAddons.addons} title={sharedAddons.title} />
          ) : null}
        </div>
      ) : null}
      {comparePlansLink ? (
        <ComparePlansLink href={comparePlansLink.href} text={comparePlansLink.text} />
      ) : null}
    </div>
  ) : null;

  return (
    <div className={styles.negativeMargin} {...props}>
      <div className={cx(styles.mobileView, isCompactLayout && styles.compactMobileView)}>
        <div className={styles.alignedCarousel}>
          <Carousel options={{ align: 'start' }} slideSize="85%" slideSpacing={0}>
            {pricePlans.map(plan => (
              <CarouselItem className={styles.pricePlanWrapper} key={plan.title}>
                <PricePlan
                  {...plan}
                  hasPromoInAnyProducts={hasPromoInAnyProducts}
                  hidePromoHighlights={hidePromoHighlights}
                  isOnboardingVariant={isOnboardingVariant}
                />
              </CarouselItem>
            ))}
            {customPricePlan ? (
              <CarouselItem className={styles.pricePlanWrapper}>
                <CustomPricePlan
                  {...customPricePlan}
                  hasPromoInAnyProducts={hasPromoInAnyProducts}
                  isOnboardingVariant={isOnboardingVariant}
                />
              </CarouselItem>
            ) : null}
          </Carousel>
        </div>
        {compactSections}
      </div>
      <div className={styles.desktopView}>
        <div className={containerClassName}>
          {pricePlans.map((plan, index) => (
            <div
              className={cx(styles.pricePlanWrapper, styles.column)}
              data-test-price-plan={index}
              key={plan.title}
            >
              <PricePlan
                {...plan}
                hasPromoInAnyProducts={hasPromoInAnyProducts}
                hidePromoHighlights={hidePromoHighlights}
                hidePromoInAllProducts={hidePromoInAllProducts}
                isOnboardingVariant={isOnboardingVariant}
              />
            </div>
          ))}
          {customPricePlan ? (
            <div className={cx(styles.pricePlanWrapper, styles.column)} data-test-price-plan={2}>
              <CustomPricePlan
                {...customPricePlan}
                hasPromoInAnyProducts={hasPromoInAnyProducts}
                hidePromoInAllProducts={hidePromoInAllProducts}
                isOnboardingVariant={isOnboardingVariant}
              />
            </div>
          ) : null}
        </div>
        {compactSections}
      </div>
    </div>
  );
}
